import Head from 'expo-router/head';
import { ScrollView } from 'react-native';
import { getTokenValue, Text, XStack, YStack } from 'tamagui';

import PSText from '@/components/ui/PSText';

/**
 * ── /components/ps-text ──────────────────────────────────────────────────
 *
 * PSText across its real role surface: all nine type roles from the
 * designer's style sheet (`type` in constants/tokens.ts), the same roles
 * re-rendered on a real Onyx surface (not a stand-in backdrop — see the
 * block below) to prove colour is a call-site prop and never baked into a
 * role, a word-level weight override for emphasis mid-sentence, the
 * `numberOfLines` truncation a bookings-list row leans on for a long title
 * AND a long address, and the `fontSize`/`lineHeight`
 * override `PSBadge`'s `size="large"` makes on `Tags`. Every block cites
 * the real screen/component that uses the pattern, the same way ps-button.tsx
 * does — this stays load-bearing, not decorative.
 *
 * One thing this preview surfaces that's worth flagging: `type.title`'s
 * `fontSize` is `'$xl2'` (28px), not the 24px the style-sheet TABLE in
 * tokens.ts's own doc comment claims for `title` — the table wasn't updated
 * when the token was. Title renders at 28/32 here, which is the TRUTH (what
 * ships), not the table's 24/32.
 * ─────────────────────────────────────────────────────────────────────────
 */

export default function PSTextPreview() {
  return (
    <>
      <Head>
        <title>PSText · Putting Scene</title>
      </Head>
      <ScrollView
        style={{
          flex: 1,
          backgroundColor: getTokenValue('$color.background', 'color'),
        }}
      >
        <YStack items="flex-start" gap="$lg" p="$lg">
          {/* Every role, light surface. Colour is passed at every call site —
            never part of a role — so each line below sets it explicitly
            rather than inheriting a default; there is no bare <PSText>. */}
          <YStack gap="$xs">
            <Text fontFamily="$body" fontSize="$small" color="$textDarkMuted">
              PSText — colour is a call-site prop, never part of a role
            </Text>
            <YStack gap="$sm">
              {/* 28/32 semibold — home&apos;s "Handpicked experiences", the
                delete-account / cancel headings, AccessPassBanner. */}
              <PSText.Title color="$textDark">
                Title — 28/32 semibold
              </PSText.Title>
              {/* 20/24 semibold — auth/email&apos;s heading, the event title
                on CheckoutEventHeader, "About"/"Tickets" on events/[id]. */}
              <PSText.H1 color="$textDark">H1 — 20/24 semibold</PSText.H1>
              {/* 20/28 medium — TicketListItem&apos;s clamped booking title,
                the payment-history day number on my-memberships/[id]. */}
              <PSText.Body1 color="$textDark">
                Body1 — 20/28 medium
              </PSText.Body1>
              {/* 18/24 semibold — profile&apos;s "Your Details" / "Upcoming
                bookings", ContactUsSheet&apos;s "Contact", PriceSummary&apos;s
                total label. */}
              <PSText.H2 color="$textDark">H2 — 18/24 semibold</PSText.H2>
              {/* 16/22 medium — the workhorse: prose, requests/[id]&apos;s
                sentences, CheckoutCart&apos;s line items. */}
              <PSText.Body2 color="$textDark">
                Body2 — 16/22 medium
              </PSText.Body2>
              {/* 14/19 medium, usually muted — auth&apos;s field labels
                ("Mobile", "First name"), ContactUsSheet&apos;s value line. */}
              <PSText.Body3 color="$textDarkMuted">
                Body3 — 14/19 medium, usually muted
              </PSText.Body3>
              {/* 12/16 semibold, tracked, UPPERCASE — my-memberships&apos;
                column labels ("Renews"/"Ends"), TicketTier&apos;s "Add"
                pill, AccessPassExclusiveHeading. Case + tracking are what
                separate it from Body4 at the same 12px. */}
              <PSText.H3 color="$textDark">
                H3 — 12/16 semibold, tracked
              </PSText.H3>
              {/* 12/16 medium — components/index.tsx&apos;s catalog blurbs,
                the payment-history note on my-memberships/[id]. */}
              <PSText.Body4 color="$textDarkMuted">
                Body4 — 12/16 medium
              </PSText.Body4>
              {/* 12/16 semibold, tracked, UPPERCASE — every PSBadge label,
                DateSelector/TimeSelector&apos;s "AP" chip. Tags actually
                deviates from the style-sheet&apos;s stated 10/14/1.20 (see
                the DEVIATIONS note above `type` in tokens.ts) — the
                dedicated 10px step was dropped, so it shares $xs (12px)
                with H3/Body4 and only tracking (1.66) sets it apart. */}
              <PSText.Tags color="$textDark">Tags — 12/16 semibold</PSText.Tags>
            </YStack>
          </YStack>

          {/* Same roles, a REAL dark surface — not a stand-in the way
            PSButton&apos;s "toolbar" demo needs one. $brandDark IS the actual
            fill behind the my-memberships hero, AccessPassBanner, and the
            purchase confirm page&apos;s plan band; textWhite/
            textWhiteMuted are the tokens those screens actually pass. */}
          <YStack bg="$color.brandDark" p="$space.xl" gap="$sm" rounded="$base">
            <Text fontFamily="$body" fontSize="$small" color="$textWhiteMuted">
              the same roles on Onyx — only the colour tokens change
              (my-memberships&apos; hero card, AccessPassBanner)
            </Text>
            <PSText.Title color="$textWhite">Access Pass Premium</PSText.Title>
            <XStack gap="$xl" items="center">
              <YStack gap="$xs">
                <PSText.H3 color="$textWhiteMuted">Renews</PSText.H3>
                <PSText.Body3 color="$textWhite">12 Oct 2026</PSText.Body3>
              </YStack>
              <YStack gap="$xs">
                <PSText.H3 color="$textWhiteMuted">Status</PSText.H3>
                <PSText.Body3 color="$textWhite">Active</PSText.Body3>
              </YStack>
            </XStack>
          </YStack>

          {/* Inline weight override — RN text doesn't cascade, but nesting a
            PSText inside another PSText DOES work the way bare RN <Text>
            nesting always has: the outer role sets the run, and one word can
            step up to $semibold for emphasis without becoming its own role
            or a new component. requests/[id]/index.tsx nests exactly this
            (Body2 inside Body2) to bold "not" inside "Your request has been
            not approved yet." — a role is size+weight+lineHeight+tracking as
            a UNIT, so overriding just fontWeight is a call-site prop, not a
            new PSText.* variant. */}
          <YStack gap="$xs">
            <Text fontFamily="$body" fontSize="$small" color="$textDarkMuted">
              inline fontWeight override for one word — requests/[id]&apos;s
              rejection copy
            </Text>
            <PSText.Body2 color="$textDark">
              Your request has{' '}
              <PSText.Body2 fontWeight="$semibold" color="$textDark">
                not
              </PSText.Body2>{' '}
              been approved yet.
            </PSText.Body2>
          </YStack>

          {/* numberOfLines — TicketListItem&apos;s row shape: a booking title
            that can run to 3 lines before clamping (Body1, $semibold), and a
            one-line address that truncates instead of wrapping the row wide
            (Body2, underlined — it&apos;s also a tap target for the map).
            Fixed-width boxes below stand in for the row&apos;s real flex-1
            column, narrow enough to force both clamps. */}
          <YStack gap="$xs">
            <Text fontFamily="$body" fontSize="$small" color="$textDarkMuted">
              numberOfLines — a long booking title clamps at 3 lines, a long
              address truncates at 1 (TicketListItem)
            </Text>
            <YStack width={220} gap="$space.xs">
              <PSText.Body1
                fontWeight="$semibold"
                color="$textDark"
                numberOfLines={3}
              >
                An Evening of Live Jazz, Comedy &amp; Cocktails at the Rooftop
                Bar
              </PSText.Body1>
              <XStack items="center" gap="$space.xs">
                <PSText.Body2
                  color="$textDark"
                  textDecorationLine="underline"
                  numberOfLines={1}
                >
                  14th Floor, Skyline Tower, Linking Road, Bandra West, Mumbai
                </PSText.Body2>
              </XStack>
            </YStack>
          </YStack>

          {/* Tags — the default 12/16 next to PSBadge&apos;s `size="large"` bump:
            that size overrides fontSize to $base (16) and lineHeight to
            20 to read as a stronger call-out than the brand/neutral chips,
            without becoming a new role — same trick as the fontWeight
            override above, on different props. */}
          <YStack gap="$xs">
            <Text fontFamily="$body" fontSize="$small" color="$textDarkMuted">
              Tags — default (every PSBadge) vs the size bump PSBadge&apos;s
              large size applies at the call site
            </Text>
            <XStack gap="$md" items="center">
              <PSText.Tags color="$textDark">Access Pass only</PSText.Tags>
              <PSText.Tags color="$textDark" fontSize="$base" lineHeight={20}>
                Sold out
              </PSText.Tags>
            </XStack>
          </YStack>
        </YStack>
      </ScrollView>
    </>
  );
}
